import React, { useState, useEffect } from 'react';
import AdminManagementService from '../../services/adminManagementService';

const RoleManagement = () => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [loading, setLoading] = useState(false);

    // Load roles and permissions
    const loadData = async () => {
        setLoading(true);
        try {
            const [rolesResponse, permissionsResponse] = await Promise.all([
                AdminManagementService.getAdminRoles(),
                AdminManagementService.getAllPermissions()
            ]);

            if (rolesResponse.success) {
                setRoles(rolesResponse.data);
            }
            if (permissionsResponse.success) {
                setPermissions(permissionsResponse.data);
            }
        } catch (error) {
            console.error('Error loading role data:', error);
            alert('Failed to load role data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    // Group permissions by category
    const groupedPermissions = permissions.reduce((acc, permission) => {
        const category = permission.category || 'other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(permission);
        return acc;
    }, {});

    const getRoleBadgeColor = (roleName) => {
        switch (roleName) {
            case 'super_admin': return 'bg-red-100 text-red-800 border-red-200';
            case 'minister': return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'support': return 'bg-green-100 text-green-800 border-green-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getPermissionDescription = (permissionKey) => {
        const descriptions = {
            'can_manage_users': 'Ban/unban tutors and students',
            'can_view_users': 'View student/tutor profiles',
            'can_manage_tutor_applications': 'Approve/reject tutor applications',
            'can_view_tutor_applications': 'View pending tutor applications',
            'can_manage_financials': 'Approve payouts and manage finances',
            'can_view_financials': 'View financial logs (read-only)',
            'can_manage_refunds': 'Approve/reject refunds',
            'can_view_refunds': 'View refund requests',
            'can_view_messages': 'Read tutor-student chats',
            'can_manage_content': 'Edit FAQs, policies, and content',
            'can_manage_admins': 'Add/remove other admins and manage roles',
            'can_view_system_logs': 'View system logs and analytics'
        };
        return descriptions[permissionKey] || permissionKey;
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">System Roles & Permissions</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        Manage role-based access control for admin users
                    </p>
                </div>
                
                {/* Roles Grid */}
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {roles.map((role) => (
                            <div key={role.id} className="border border-gray-200 rounded-lg p-4">
                                {/* Role Header */}
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-lg font-medium text-gray-900 capitalize">
                                        {role.name.replace('_', ' ')}
                                    </h3>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(role.name)}`}>
                                        {role.admin_count || 0} admins
                                    </span>
                                </div>

                                {/* Role Description */}
                                {role.description && (
                                    <p className="text-sm text-gray-600 mb-4">
                                        {role.description}
                                    </p>
                                )}

                                {/* Permissions List */}
                                <div className="space-y-2">
                                    <h4 className="text-sm font-medium text-gray-700">Permissions:</h4>
                                    {role.permissions && role.permissions.length > 0 ? (
                                        <ul className="space-y-1">
                                            {role.permissions.map((permission, index) => (
                                                <li key={index} className="flex items-start">
                                                    <svg className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="text-sm text-gray-600">
                                                        {getPermissionDescription(permission)}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-gray-500 italic">No permissions assigned</p>
                                    )}
                                </div>

                                {/* Super Admin Badge */}
                                {role.is_super_admin && (
                                    <div className="mt-3 pt-3 border-t border-gray-200">
                                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                            Super Admin Role
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Permissions Reference */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Available Permissions</h2>
                    <p className="text-sm text-gray-600 mt-1">
                        All system permissions that can be assigned to roles
                    </p>
                </div>
                
                <div className="p-6">
                    {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                        <div key={category} className="mb-6 last:mb-0">
                            <h3 className="text-md font-medium text-gray-900 mb-3 capitalize">
                                {category.replace('_', ' ')} Permissions
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {categoryPermissions.map((permission) => (
                                    <div key={permission.id} className="border border-gray-200 rounded-md p-3">
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-900">
                                                    {permission.permission_key.replace(/_/g, ' ')}
                                                </h4>
                                                <p className="text-sm text-gray-600 mt-1">
                                                    {permission.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default RoleManagement;